import {
  Component,
  OnInit,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import { OrderSensorComponent } from 'src/shared/components/order-sensor/order-sensor.component';

type HardwareType = 'noise' | 'wifi';

@Component({
  selector: 'hardware-info-page',
  templateUrl: 'hardware-info.page.html',
  standalone: true,
  imports: [CommonModule, TidyUiComponentsModule, SharedModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class HardwareInfoPage implements OnInit {
  private readonly navCtrl = inject(CustomNavController);
  private readonly storage = inject(TidyStorage);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly cdr = inject(ChangeDetectorRef);
  readonly windowService = inject(WindowService);

  hardwareType: HardwareType = 'noise';
  pageTitle = 'TIDY Sensor';
  boundOrderSensor = this.orderSensor.bind(this);

  readonly description = 'A custom Ubiquiti Wi-Fi 7 router combined with a noise sensor, configured with TIDY software to deliver low-cost guest Wi-Fi that captures guest information, and Airbnb-compliant noise monitoring — all in one device. $250 per sensor, including tax and shipping.';

  readonly features = [
    'Branded guest Wi-Fi splash page with contact capture',
    'Airbnb-compliant noise monitoring',
    'Privacy-safe — no audio recording',
    'Real-time noise alerts and historical tracking',
    'Wi-Fi 7 with 1,250 ft\u00B2 coverage',
    '200+ connected devices supported',
  ];

  readonly specs: { label: string; value: string }[] = [
    { label: 'Wi-Fi Technology', value: 'Wi-Fi 7, Dual-radio 2.4/5 GHz, 4 spatial streams' },
    { label: '6 GHz', value: 'Extended Range Mode' },
    { label: 'Coverage', value: '1,250 ft\u00B2 (115 m\u00B2)' },
    { label: 'Connected Devices', value: '200+' },
    { label: 'Dimensions', value: '\u00D8171.5 x 33 mm (\u00D86.8 x 1.3\u2033)' },
    { label: 'Power', value: 'PoE (Power over Ethernet)' },
    { label: 'Uplink', value: '2.5/1 GbE' },
    { label: 'Mounting', value: 'Ceiling or wall' },
  ];

  readonly faqs: { question: string; answer: string }[] = [
    { question: 'How many sensors do I need?', answer: 'Each sensor covers about 1,250 ft\u00B2. We recommend one per property for most rentals. Larger properties may need additional sensors for full coverage.' },
    { question: 'How is it powered?', answer: 'The sensor uses Power over Ethernet (PoE). You can power it with a PoE switch or the included PoE adapter plugged into a standard outlet.' },
    { question: 'How does noise monitoring work?', answer: 'The sensor measures ambient noise levels without recording audio. It detects when noise exceeds configurable thresholds and sends real-time alerts, keeping you Airbnb compliant.' },
    { question: 'How does guest Wi-Fi capture work?', answer: 'Guests connect to your branded Wi-Fi splash page and provide their contact information to get online. You can use this to drive reviews and direct bookings.' },
    { question: 'Can I mesh multiple sensors together?', answer: 'Yes, sensors automatically form a mesh network for seamless coverage across larger properties without additional cabling.' },
  ];

  async ngOnInit() {
    this.hardwareType = this.navCtrl.getParam('hardwareType', false) || 'noise';
    if (!this.hardwareType) {
      const dialogParams = await this.storage.retrieve('dialogParams');
      this.hardwareType = dialogParams?.hardwareType || 'noise';
    }
    this.cdr.markForCheck();
  }

  orderSensor() {
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.navigateTo('order-sensor', { sourceContext: this.hardwareType }, OrderSensorComponent);
    } else {
      this.navCtrl.navigateForward('order-sensor', { sourceContext: this.hardwareType });
    }
  }

  goBack() {
    const backRoute = this.hardwareType === 'wifi' ? 'revenue' : 'compliance';
    this.navCtrl.navigateBack(backRoute);
  }
}
